if (!customElements.get('sleek-product')) {
  customElements.define('sleek-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.enableSelectors();
      this.showMedia(this.querySelector('[data-initial-media]')?.dataset.mediaId);
      this.initializeModels();
      this.addEventListener('submit', (event) => {
        if (this.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          return;
        }
        if (event.submitter?.name === 'add') {
          event.preventDefault();
          this.addToCart(event.target);
        }
      });
      this.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-media-target]');
        if (!thumbnail) return;
        event.preventDefault();
        this.showMedia(thumbnail.dataset.mediaTarget);
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-product-option], [data-selling-plan]')) this.updateSelection(event.target);
      });
    }

    disconnectedCallback() { this.request?.abort(); }

    enableSelectors() {
      this.querySelectorAll('[data-product-option], [data-selling-plan]').forEach((select) => { select.disabled = false; });
    }

    async addToCart(form) {
      const button = form.querySelector('[name="add"]');
      const id = form.querySelector('[name="id"]');
      if (!id?.value || id.disabled || button?.disabled) return;
      const formData = new FormData(form);
      const controls = [...form.querySelectorAll('[data-product-option], [data-selling-plan], [name="quantity"]')];
      controls.forEach((control) => { control.disabled = true; });
      const paymentButton = form.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.hidden = true;
      if (button) button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      const errorElement = this.querySelector('[data-product-error]');
      if (errorElement) errorElement.hidden = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
        const result = await response.json();
        if (!response.ok || result.status) {
          if (errorElement) {
            errorElement.textContent = typeof result.description === 'string' ? result.description : this.dataset.addError;
            errorElement.hidden = false;
          }
          if (button) button.disabled = false;
          return;
        }
        location.assign(this.dataset.cartUrl);
      } catch {
        if (errorElement) {
          errorElement.textContent = this.dataset.addError;
          errorElement.hidden = false;
        }
        if (button) button.disabled = false;
      } finally {
        controls.forEach((control) => { control.disabled = false; });
        if (paymentButton && !button?.disabled) paymentButton.hidden = false;
        this.removeAttribute('aria-busy');
      }
    }

    initializeModels() {
      if (!this.querySelector('model-viewer') || !window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors || !this.isConnected) return;
          this.querySelectorAll('model-viewer').forEach((model) => {
            if (!model.dataset.initialized) {
              model.dataset.initialized = 'true';
              new window.Shopify.ModelViewerUI(model);
            }
          });
        }
      }]);
    }

    showMedia(id) {
      const panels = [...this.querySelectorAll('[data-media-id]')];
      const selectedId = id || panels[0]?.dataset.mediaId;
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.mediaId !== selectedId;
        if (panel.hidden) {
          panel.querySelectorAll('video').forEach((video) => video.pause());
          panel.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
          panel.querySelectorAll('iframe').forEach((frame) => {
            let origin;
            try { origin = new URL(frame.src).origin; } catch { return; }
            frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), origin);
            frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), origin);
          });
        }
      });
      this.querySelectorAll('[data-media-target]').forEach((thumbnail) => {
        if (thumbnail.dataset.mediaTarget === selectedId) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
    }

    async updateSelection(control) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const selectedOption = control.matches('[data-product-option]') ? control.selectedOptions[0] : null;
      const productUrl = selectedOption?.dataset.productUrl || this.dataset.productUrl;
      const url = new URL(productUrl, location.origin);
      const values = [...this.querySelectorAll('[data-product-option]')].map((select) => select.value);
      if (values.length) url.searchParams.set('option_values', values.join(','));
      const plan = this.querySelector('[data-selling-plan]')?.value;
      if (plan) url.searchParams.set('selling_plan', plan);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const focusId = control.id;
      const button = this.querySelector('[name="add"]');
      if (button) button.disabled = true;
      const paymentButton = this.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.hidden = true;
      const idInput = this.querySelector('[name="id"]');
      if (idInput) idInput.disabled = true;
      this.setAttribute('aria-busy', 'true');
      const errorElement = this.querySelector('[data-product-error]');
      if (errorElement) errorElement.hidden = true;

      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Product selection request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = document.querySelector('sleek-product');
        if (!updated) throw new Error('Product section missing');
        if (request.signal.aborted) return;
        const quantity = Number(this.querySelector('[name="quantity"]')?.value || 1);
        this.innerHTML = updated.innerHTML;
        this.dataset.productUrl = updated.dataset.productUrl;
        this.enableSelectors();
        this.showMedia(this.querySelector('[data-initial-media]')?.dataset.mediaId);
        this.initializeModels();
        const input = this.querySelector('[name="quantity"]');
        if (input) {
          const min = Number(input.min || 1);
          const max = Number(input.max || Infinity);
          const step = Number(input.step || 1);
          input.value = String(Math.min(max, min + Math.max(0, Math.ceil((quantity - min) / step)) * step));
        }
        url.searchParams.delete('section_id');
        const variantId = this.querySelector('[name="id"]')?.value;
        if (variantId) {
          url.searchParams.delete('option_values');
          url.searchParams.set('variant', variantId);
        }
        history.replaceState({}, '', url);
        this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
        if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A selection that failed to resolve must never submit the previous variant.
        const errorElement = this.querySelector('[data-product-error]');
        if (errorElement) {
          errorElement.textContent = this.dataset.updateError;
          errorElement.hidden = false;
        }
        const paymentButton = this.querySelector('.shopify-payment-button');
        if (paymentButton) paymentButton.hidden = true;
        const idInput = this.querySelector('[name="id"]');
        if (idInput) idInput.disabled = true;
        if (button) button.disabled = true;
      } finally {
        if (this.request === request) this.removeAttribute('aria-busy');
      }
    }
  });
}
